import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
    TextField,
    Button,
    Card,
    CardContent,
    CardMedia,
    IconButton,
    ImageList,
    ImageListItem,
    FormControl, Select, OutlinedInput
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {CiImageOn, CiSquarePlus} from 'react-icons/ci';
import {getDoc, doc, updateDoc, setDoc} from 'firebase/firestore';
import {ref, uploadBytes, getDownloadURL} from 'firebase/storage';
import {db, storage, eventsCollectionName, deleteFileByUrl} from "../../components/Firebase/Firebase";
import BadgePreview from "./badges/BadgePreview";
import {useLoading} from "../../components/context/LoadingContext";
import CustomDatePicker from "../../components/UI/datepicker/DatePicker";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import MenuItem from "@mui/material/MenuItem";

const EventEditPage = () => {
    const {eventId} = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useLoading();

    const initialEventState = {
        id: '',
        title: '',
        startDate: '2024-06-15T08:00:00+03:00',
        finishDate: '2024-06-16T08:00:00+03:00',
        imageUrl: '',
        previewImageUrl: '',
        addressInfo: {
            address: "г. Воронеж, Белый Колодец",
            location: {
                latitude: 50.537,
                longitude: 36.537,
            },
            descriptionTitle: "Экспо, старт и финиш для всех дистанций: Отель «ГТК Суздаль»",
            description: "«Sprint GRUT 5K»: г. Суздаль, Ильинский луг, ул. Покровская, автобусная остановка «Корпус Института», смотровая площадка",
            addressDetailed: "Суздаль. ул. Коровники 45",
        },
        eventProgram: [
            {
                date: "17.07.2021",
                schedule: [
                    {
                        time: "00:00",
                        stage: "Старт дистанции «Hard Night»"
                    },
                    {
                        time: "02:00",
                        stage: "Закрытие финиша дистанции «Hard Night»"
                    }
                ]
            },
            {
                date: "18.07.2021",
                schedule: [
                    {
                        time: "00:00",
                        stage: "Старт дистанции «Hard Night»"
                    },
                    {
                        time: "02:00",
                        stage: "Закрытие финиша дистанции «Hard Night»"
                    }
                ]
            },
        ],
        eventInfo: {
            descriptionTitle: "Ночной ультрамарафон Crazy Owl 50 Marina Tsvetaeva",
            description: [
                "Crazy Owl — бег по пересеченной местности элементами кросса и дистанциями на любой вкус.",
                "Город Суздаль не случайно выбран местом для проведения мероприятия."
            ]
        },
        media: [],
        isEnabled: false
    };

    const [eventItem, setEventItem] = useState(initialEventState);
    const [previewImage, setPreviewImage] = useState(null);
    const [image, setImage] = useState(null);
    const [oldUrls, setOldUrls] = useState([]);
    const [locationErrors, setLocationErrors] = useState({
        latitude: '',
        longitude: ''
    });

    const onPreviewFileChange = (event) => {
        setPreviewImage(event.target.files[0]);
    };

    const onImageFileChange = (event) => {
        setImage(event.target.files[0]);
    };

    useEffect(() => {
        const initialize = async () => {
            if (eventId === 'new') {
                setEventItem(initialEventState);
            } else {
                await fetchEventItem();
            }
        };

        initialize();
    }, [eventId]);

    const fetchEventItem = async () => {
        setIsLoading(true);
        try {
            const docRef = doc(db, eventsCollectionName, eventId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                setEventItem({
                    ...initialEventState,
                    ...data,
                    addressInfo: data.addressInfo || initialEventState.addressInfo,
                    eventProgram: data.eventProgram || initialEventState.eventProgram,
                    eventInfo: data.eventInfo || initialEventState.eventInfo,
                    media: data.media || initialEventState.media
                });

                const tempUrls = [];

                if (data.media && data.media.length > 0) {
                    tempUrls.push(...data.media.map(item => item.url));
                }
                if (data.imageUrl) {
                    tempUrls.push(data.imageUrl);
                }
                if (data.previewImageUrl) {
                    tempUrls.push(data.previewImageUrl);
                }

                setOldUrls(tempUrls);
            } else {
                console.error("No such document!");
            }
        } catch (error) {
            console.error("Error fetching document: ", error);
        }
        setIsLoading(false);
    };


    const handleProgramChange = (index, key, value) => {
        const updatedProgram = [...eventItem.eventProgram];
        updatedProgram[index][key] = value;
        setEventItem({...eventItem, eventProgram: updatedProgram});
    };

    const handleAddProgram = () => {
        setEventItem({
            ...eventItem,
            eventProgram: [
                ...eventItem.eventProgram,
                {date: "", schedule: [{time: "", stage: ""}]}
            ]
        });
    };

    const handleRemoveProgram = (index) => {
        const updatedProgram = eventItem.eventProgram.filter((_, i) => i !== index);
        setEventItem({...eventItem, eventProgram: updatedProgram});
    };

    const handleScheduleChange = (programIndex, scheduleIndex, key, value) => {
        const updatedProgram = [...eventItem.eventProgram];
        updatedProgram[programIndex].schedule[scheduleIndex][key] = value;
        setEventItem({...eventItem, eventProgram: updatedProgram});
    };

    const handleAddSchedule = (programIndex) => {
        const updatedProgram = [...eventItem.eventProgram];
        updatedProgram[programIndex].schedule.push({time: "", stage: ""});
        setEventItem({...eventItem, eventProgram: updatedProgram});
    };

    const handleRemoveSchedule = (programIndex, scheduleIndex) => {
        const updatedProgram = [...eventItem.eventProgram];
        updatedProgram[programIndex].schedule = updatedProgram[programIndex].schedule.filter((_, i) => i !== scheduleIndex);
        setEventItem({...eventItem, eventProgram: updatedProgram});
    };

    const handleLocationChange = (key, value) => {
        setLocationErrors(prev => ({
            ...prev,
            [key]: ''
        }));
        setEventItem(prev => ({
            ...prev,
            addressInfo: {
                ...prev.addressInfo,
                location: {
                    ...prev.addressInfo.location,
                    [key]: value === "" ? null : (isNaN(Number(value)) ? null : Number(value))
                }
            }
        }));
    };

    const handleDescriptionChange = (index, value) => {
        const updatedDescriptions = [...eventItem.eventInfo.description];
        updatedDescriptions[index] = value;
        setEventItem({...eventItem, eventInfo: {...eventItem.eventInfo, description: updatedDescriptions}});
    };

    const handleAddDescription = () => {
        setEventItem({
            ...eventItem,
            eventInfo: {...eventItem.eventInfo, description: [...eventItem.eventInfo.description, '']}
        });
    };

    const handleEditDistances = () => {
        navigate(`/events/${eventId}/distances`, { state: { eventId } });
    };

    const handleEditTimingSettings = () => {
        navigate(`/events/${eventId}/timing`, { state: { eventId } });
    };


    const handleRemoveDescription = (index) => {
        const updatedDescriptions = eventItem.eventInfo.description.filter((_, i) => i !== index);
        setEventItem({...eventItem, eventInfo: {...eventItem.eventInfo, description: updatedDescriptions}});
    };

    const handleMediaChange = (event, index) => {
        const file = event.target.files[0];
        if (file) {
            const newMedia = [...eventItem.media];
            const fileType = file.type.startsWith('image/') ? 'image' : 'video';
            const newMediaItem = {
                type: fileType,
                url: file
            };

            if (index !== null) {
                newMedia[index] = newMediaItem;
            } else {
                newMedia.push(newMediaItem);
            }

            setEventItem({...eventItem, media: newMedia});
        }
    };

    const handleStatusChange = (e) => {
        setEventItem({...eventItem, isEnabled: e.target.value === 'true'});
    };

    const handleRemoveMedia = (index) => {
        const newMedia = eventItem.media.filter((_, i) => i !== index);
        setEventItem({...eventItem, media: newMedia});
    };

    const uploadFile = async (file, folder) => {
        const storageRef = ref(storage, `${eventsCollectionName}/${eventItem.id}/${folder}/` + file.name);
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
    };

    const validateLocation = () => {
        const latitudeValue = eventItem.addressInfo.location.latitude;
        const longitudeValue = eventItem.addressInfo.location.longitude;
        const nextErrors = {
            latitude: latitudeValue == null || isNaN(latitudeValue)
                ? 'Укажите широту'
                : '',
            longitude: longitudeValue == null || isNaN(longitudeValue)
                ? 'Укажите долготу'
                : ''
        };

        setLocationErrors(nextErrors);

        return !nextErrors.latitude && !nextErrors.longitude;
    };

    const handleUpdateEvent = async (e) => {
        e.preventDefault();

        if (!validateLocation()) {
            return;
        }

        setIsLoading(true);

        if (image) {
            eventItem.imageUrl = await uploadFile(image, 'main');
        }
        if (previewImage) {
            eventItem.previewImageUrl = await uploadFile(previewImage, 'preview');
        }

        const mediaList = await Promise.all(
            eventItem.media.map(async (mediaItem) => {
                if (typeof mediaItem.url === 'string') {
                    return mediaItem;
                } else {
                    const fileUrl = await uploadFile(mediaItem.url, 'media');
                    return { type: mediaItem.type, url: fileUrl };
                }
            })
        );


        const newEvent = {
            ...eventItem,
            media: mediaList,
        };

        const tempNewUrls = [];

        if (newEvent.media && newEvent.media.length > 0) {
            newEvent.media.forEach(item => tempNewUrls.push(item.url));
        }
        if (newEvent.imageUrl) {
            tempNewUrls.push(newEvent.imageUrl);
        }
        if (newEvent.previewImageUrl) {
            tempNewUrls.push(newEvent.previewImageUrl);
        }

        try {
            if (eventId === 'new') {
                await setDoc(doc(db, eventsCollectionName, newEvent.id), newEvent);
                console.log('Document written with ID:', newEvent.id);
            } else {
                await updateDoc(doc(db, eventsCollectionName, eventId), newEvent);
                const urlsToDelete = oldUrls.filter(url => !tempNewUrls.includes(url));

                for (const url of urlsToDelete) {
                    await deleteFileByUrl(url);
                }
                console.log('Event updated with ID:', eventId);
            }
        } catch (error) {
            console.error('Error adding/updating document:', error);
        }

        setIsLoading(false);
        navigate('/events');
    };


    return (
        <div className='right-content'>
            <form>
                <div className='card shadow border-0'>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className='hd mb-0'>Старт: </h4>
                        {eventId !== 'new' && (
                            <Button className='logInButton' variant="contained" onClick={handleEditTimingSettings}>
                                Настройки хронометража
                            </Button>
                        )}
                    </div>
                    <div className='form-group'>
                        <div>
                            <h5>Статус:</h5>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <FormControl
                                className="w-auto"
                                sx={{
                                    minWidth: 200,
                                    marginRight: 5,
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#CEFC4C',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#CEFC4C',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#CEFC4C',
                                        },
                                    },
                                }}
                            >
                                <Select
                                    value={eventItem.isEnabled.toString()}
                                    onChange={handleStatusChange}
                                    input={<OutlinedInput/>}
                                    sx={{
                                        color: '#fff',
                                        '& .MuiSvgIcon-root': {
                                            color: '#fff',
                                        },
                                    }}
                                >
                                    <MenuItem value="false">Не доступно</MenuItem>
                                    <MenuItem value="true">Доступно</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                    </div>
                    {/* Title and id */}
                    <div className='row form-group'>
                        <div className='col'>
                            <TextField
                                className='w-100'
                                value={eventItem.id || ''}
                                onChange={e => setEventItem({...eventItem, id: e.target.value})}
                                label="Id"
                                InputProps={{
                                    readOnly: eventId !== 'new',
                                }}
                                sx={textFieldStyles}
                            />
                        </div>
                        <div className='col'>
                            <TextField
                                className='w-100'
                                value={eventItem.title || ''}
                                onChange={e => setEventItem({...eventItem, title: e.target.value})}
                                label="Название"
                                sx={textFieldStyles}
                            />
                        </div>
                    </div>
                    {/* Dates */}
                    <div style={{marginBottom: 10}}>
                        <h5>Даты:</h5>
                    </div>
                    <div className='row form-group'>
                        <div className='col'>
                            <CustomDatePicker
                                label="Дата начала"
                                value={eventItem.startDate || ''}
                                onChange={(isoDate) => setEventItem({...eventItem, startDate: isoDate})}
                            />
                        </div>
                        <div className='col'>
                            <CustomDatePicker
                                label="Дата конца"
                                value={eventItem.finishDate || ''}
                                onChange={(isoDate) => setEventItem({...eventItem, finishDate: isoDate})}
                            />
                        </div>
                    </div>
                    {/* Images */}
                    <div className='row form-group'>
                        <div className='col'>
                            <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
                                {/* Preview Image */}
                                <div>
                                    <div style={{marginTop: 10}}>
                                        <h5>Превью изображение:</h5>
                                    </div>
                                    <div style={{width: 312}}>
                                        <Card
                                            sx={{width: 312, height: 214}}
                                            className='mediaCard'
                                        >
                                            {previewImage ? (
                                                <CardMedia
                                                    component="img"
                                                    sx={{width: '100%', height: '100%', objectFit: 'crop'}}
                                                    src={URL.createObjectURL(previewImage)}
                                                    alt="Preview Image"
                                                />
                                            ) : eventItem.previewImageUrl ? (
                                                <CardMedia
                                                    component="img"
                                                    sx={{width: '100%', height: '100%', objectFit: 'crop'}}
                                                    src={eventItem.previewImageUrl}
                                                    alt="Preview Image"
                                                />
                                            ) : (
                                                <CardContent
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        height: '100%',
                                                    }}
                                                >
                                                    <CiImageOn size={40}/>
                                                </CardContent>
                                            )}
                                        </Card>
                                        <Button
                                            className='logInButton w-100'
                                            variant="contained"
                                            component="label"
                                            sx={{marginTop: 2}}
                                        >
                                            Изменить
                                            <input
                                                type="file"
                                                hidden
                                                onChange={onPreviewFileChange}
                                            />
                                        </Button>
                                    </div>
                                </div>
                                {/* Badge */}
                                {eventId !== 'new' && (
                                    <div style={{ marginTop: 10 }}>
                                        <h5>Плашка события:</h5>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, marginTop: 8 }}>
                                            {eventItem.activeBadge ? (
                                                <BadgePreview
                                                    text={eventItem.activeBadge.text}
                                                    textColor={eventItem.activeBadge.textColor}
                                                    backgroundColor={eventItem.activeBadge.backgroundColor}
                                                />
                                            ) : (
                                                <span style={{ color: '#888' }}>Нет активной плашки</span>
                                            )}
                                            <Button className='logInButton' onClick={() => navigate(`/events/${eventId}/badges`)}>
                                                Управлять плашками
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className='col'>
                            <div style={{marginTop: 10}}>
                                <h5>Основное изображение:</h5>
                            </div>
                            <div style={{width: 312}}>
                                <Card
                                    sx={{width: 312, height: 214}}
                                    className='mediaCard'
                                >
                                    {image ? (
                                        <CardMedia
                                            component="img"
                                            sx={{width: '100%', height: '100%', objectFit: 'crop'}}
                                            src={URL.createObjectURL(image)}
                                            alt="Image"
                                        />
                                    ) : eventItem.imageUrl ? (
                                        <CardMedia
                                            component="img"
                                            sx={{width: '100%', height: '100%', objectFit: 'crop'}}
                                            src={eventItem.imageUrl}
                                            alt="Image"
                                        />
                                    ) : (
                                        <CardContent
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: '100%',
                                            }}
                                        >
                                            <CiImageOn size={40}/>
                                        </CardContent>
                                    )}
                                </Card>
                                <Button
                                    className='logInButton w-100'
                                    variant="contained"
                                    component="label"
                                    sx={{marginTop: 2}}
                                >
                                    Изменить
                                    <input
                                        type="file"
                                        hidden
                                        onChange={onImageFileChange}
                                    />
                                </Button>
                            </div>
                        </div>
                    </div>
                    {/* Address Info */}
                    <div style={{marginBottom: 10}}>
                        <h5>Детали адреса:</h5>
                    </div>
                    <div className='form-group'>
                        <div className='row form-group'>
                            <div className='col'>
                                <TextField
                                    className='w-100'
                                    value={eventItem.addressInfo.address || ''}
                                    onChange={e => setEventItem({
                                        ...eventItem,
                                        addressInfo: {...eventItem.addressInfo, address: e.target.value}
                                    })}
                                    label="Адрес"
                                    sx={textFieldStyles}
                                />
                            </div>
                            <div className='col'>
                                <TextField
                                    className='w-100'
                                    value={eventItem.addressInfo.addressDetailed || ''}
                                    onChange={e => setEventItem({
                                        ...eventItem,
                                        addressInfo: {...eventItem.addressInfo, addressDetailed: e.target.value}
                                    })}
                                    label="Детальный адрес"
                                    sx={textFieldStyles}
                                />
                            </div>
                        </div>
                        <div className='row form-group'>
                            <div className='col'>
                                <TextField
                                    className='w-100'
                                    type="number"
                                    value={eventItem.addressInfo.location.latitude ?? ""}
                                    onChange={e => handleLocationChange("latitude", e.target.value)}
                                    label="Широта"
                                    required
                                    error={Boolean(locationErrors.latitude)}
                                    helperText={locationErrors.latitude}
                                    sx={textFieldStyles}
                                />
                            </div>
                            <div className='col'>
                                <TextField
                                    className='w-100'
                                    type="number"
                                    value={eventItem.addressInfo.location.longitude ?? ""}
                                    onChange={e => handleLocationChange("longitude", e.target.value)}
                                    label="Долгота"
                                    required
                                    error={Boolean(locationErrors.longitude)}
                                    helperText={locationErrors.longitude}
                                    sx={textFieldStyles}
                                />
                            </div>
                        </div>
                        <TextField
                            className='form-group w-75'
                            value={eventItem.addressInfo.descriptionTitle || ''}
                            onChange={e => setEventItem({
                                ...eventItem,
                                addressInfo: {...eventItem.addressInfo, descriptionTitle: e.target.value}
                            })}
                            label="Заголовок описания"
                            sx={textFieldStyles}
                        />
                        <TextField
                            className='form-group w-100'
                            value={eventItem.addressInfo.description || ''}
                            onChange={e => setEventItem({
                                ...eventItem,
                                addressInfo: {...eventItem.addressInfo, description: e.target.value}
                            })}
                            label="Описания"
                            multiline
                            rows={2}
                            sx={textFieldStyles}
                        />
                    </div>

                    {/* Event program */}
                    <div>
                        <h5>Программа мероприятия:</h5>
                    </div>
                    <div className='form-group'>
                        {eventItem.eventProgram.map((program, programIndex) => (
                            <div key={programIndex} className='program-section'>
                                <Button
                                    className='logInButton'
                                    style={{width: 150}}
                                    variant="contained"
                                    component="label"
                                    onClick={() => handleRemoveProgram(programIndex)}
                                >
                                    Удалить
                                </Button>
                                <div style={{marginTop: 20}}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
                                        <DatePicker
                                            className='w-25'
                                            label="Дата"
                                            value={dayjs(program.date, "DD.MM.YYYY")}
                                            onChange={(newValue) => {
                                                const formattedDate = newValue.format("DD.MM.YYYY");
                                                handleProgramChange(programIndex, 'date', formattedDate);
                                            }}
                                            inputFormat="DD.MM.YYYY"
                                            sx={textFieldStyles}
                                        />
                                    </LocalizationProvider>
                                </div>
                                <ImageList cols={50} gap={20}
                                           style={{flexWrap: 'nowrap', overflowX: 'auto', marginTop: 20}}>
                                    {program.schedule.map((scheduleItem, scheduleIndex) => (
                                        <ImageListItem key={scheduleIndex} style={{width: 400}}>
                                            <Card
                                                style={{
                                                    backgroundColor: "transparent",
                                                    borderColor: '#CEFC4C',
                                                    borderStyle: "solid",
                                                }}
                                                sx={{width: '100%', height: 'auto'}}
                                            >
                                                <CardContent>
                                                    <TextField
                                                        className='w-100'
                                                        style={{marginTop: 15}}
                                                        value={scheduleItem.time}
                                                        onChange={e => handleScheduleChange(programIndex, scheduleIndex, 'time', e.target.value)}
                                                        label="Время"
                                                        sx={textFieldStyles}
                                                    />
                                                    <TextField
                                                        className='w-100'
                                                        style={{marginTop: 15}}
                                                        value={scheduleItem.stage}
                                                        onChange={e => handleScheduleChange(programIndex, scheduleIndex, 'stage', e.target.value)}
                                                        label="Этап"
                                                        multiline
                                                        rows={4}
                                                        sx={textFieldStyles}
                                                    />
                                                    <Button
                                                        className='logInButton w-100'
                                                        variant="contained"
                                                        component="label"
                                                        onClick={() => handleRemoveSchedule(programIndex, scheduleIndex)}
                                                    >
                                                        Удалить
                                                    </Button>
                                                </CardContent>
                                            </Card>
                                        </ImageListItem>
                                    ))}
                                    <ImageListItem style={{width: 400, height: 'auto'}}>
                                        <Card
                                            sx={{width: '100%', height: '100%', cursor: 'pointer'}}
                                            className='mediaCard'
                                            onClick={() => handleAddSchedule(programIndex)}
                                        >
                                            <CardContent
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: '100%',
                                                }}
                                            >
                                                <CiSquarePlus size={80}/>
                                            </CardContent>
                                        </Card>
                                    </ImageListItem>
                                </ImageList>
                            </div>
                        ))}
                        <Button
                            className='logInButton w-100'
                            variant="contained"
                            component="label"
                            sx={{marginTop: 2}}
                            onClick={handleAddProgram}
                        >
                            Добавить программу
                        </Button>
                    </div>

                    {/* Description Info */}
                    <div style={{marginBottom: 10}}>
                        <h5>Описание события:</h5>
                    </div>
                    <div className='form-group'>
                        <TextField
                            className='form-group w-100'
                            value={eventItem.eventInfo.descriptionTitle || ''}
                            onChange={e => setEventItem({
                                ...eventItem,
                                eventInfo: {...eventItem.eventInfo, descriptionTitle: e.target.value}
                            })}
                            label="Заголовок описания"
                            sx={textFieldStyles}
                        />
                        {eventItem.eventInfo.description.map((desc, index) => (
                            <div key={index}>
                                <Button
                                    className='logInButton'
                                    style={{width: 150}}
                                    variant="contained"
                                    component="label"
                                    sx={{marginTop: 2}}
                                    onClick={() => handleRemoveDescription(index)}
                                >
                                    Удалить
                                </Button>
                                <TextField
                                    className='w-100'
                                    style={{marginTop: 20}}
                                    value={desc}
                                    onChange={e => handleDescriptionChange(index, e.target.value)}
                                    label={`Описание ${index + 1}`}
                                    multiline
                                    rows={2}
                                    sx={textFieldStyles}
                                />
                            </div>
                        ))}
                        <Button
                            className='logInButton w-100'
                            variant="contained"
                            component="label"
                            sx={{marginTop: 2}}
                            onClick={handleAddDescription}
                        >
                            Добавить описание
                        </Button>
                    </div>

                    {/* Media */}
                    <div style={{marginBottom: 10}}>
                        <h5>Медиа:</h5>
                    </div>
                    <div className='form-group'>
                        <ImageList cols={50} gap={20} style={{flexWrap: 'nowrap', overflowX: 'auto'}}>
                            {eventItem.media.map((mediaItem, index) => (
                                <ImageListItem key={index} style={{width: 312}}>
                                    <Card
                                        sx={{width: '100%', height: 214}}
                                        className='mediaCard'
                                    >
                                        <IconButton
                                            aria-label="delete"
                                            size="small"
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                zIndex: 1,
                                                background: 'rgba(0, 0, 0, 0.5)',
                                                color: '#fff',
                                            }}
                                            onClick={() => handleRemoveMedia(index)}
                                        >
                                            <DeleteIcon/>
                                        </IconButton>
                                        {mediaItem.type === 'video' ? (
                                            <CardMedia
                                                component="video"
                                                sx={{width: '100%', height: '100%', objectFit: 'crop'}}
                                                src={typeof mediaItem.url === 'string' ? mediaItem.url : URL.createObjectURL(mediaItem.url)}
                                                controls
                                                alt="media preview"
                                            />
                                        ) : (
                                            <CardMedia
                                                component="img"
                                                sx={{width: '100%', height: '100%', objectFit: 'crop'}}
                                                src={typeof mediaItem.url === 'string' ? mediaItem.url : URL.createObjectURL(mediaItem.url)}
                                                alt="media preview"
                                            />
                                        )}
                                    </Card>
                                    <Button
                                        className='logInButton w-100'
                                        variant="contained"
                                        component="label"
                                        sx={{marginTop: 2}}
                                    >
                                        Изменить
                                        <input
                                            type="file"
                                            hidden
                                            onChange={(e) => handleMediaChange(e, index)}
                                        />
                                    </Button>
                                </ImageListItem>
                            ))}
                            <ImageListItem style={{width: 312, height: 214}}>
                                <Card
                                    sx={{width: '100%', height: '100%'}}
                                    className='mediaCard'
                                    onClick={() => document.getElementById('file-upload-input').click()}
                                >
                                    <CardContent
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <CiImageOn size={40}/>
                                    </CardContent>
                                </Card>
                                <input
                                    id="file-upload-input"
                                    type="file"
                                    hidden
                                    onChange={(e) => handleMediaChange(e, null)}
                                />
                            </ImageListItem>
                        </ImageList>
                    </div>
                    <Button
                        className='logInButton w-100'
                        variant="contained"
                        component="label"
                        type="button"
                        onClick={handleUpdateEvent}
                    >
                        Сохранить
                    </Button>
                </div>
            </form>
        </div>
    );
};

const textFieldStyles = {
    '& .MuiSvgIcon-root': {
        color: '#fff',
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: '#CEFC4C',
        },
        '&:hover fieldset': {
            borderColor: '#CEFC4C',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#CEFC4C',
        },
        '& input': {
            color: '#fff',
        },
        '& textarea': {
            color: '#fff',
        },
    },
    '& .MuiInputLabel-root': {
        color: '#fff',
        '&.Mui-focused': {
            color: '#fff',
        },
    },
};

export default EventEditPage;
